import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { api } from "./lib/api";
import Landing from "./pages/Landing";
import Profiles from "./pages/Profiles";
import WorldMap from "./pages/WorldMap";
import LessonPlayer from "./pages/LessonPlayer";
import Rewards from "./pages/Rewards";
import ParentDashboard from "./pages/ParentDashboard";
import Admin from "./pages/Admin";
import Learn from "./pages/Learn";
import Stories from "./pages/Stories";
import Contact from "./pages/Contact";
import UserHub from "./pages/UserHub";
import Recordings from "./pages/Recordings";
import Listen from "./pages/Listen";
import Halls from "./pages/Halls";
import ZhuyinRoom from "./pages/zhuyin/ZhuyinRoom";
import ZhuyinLearn from "./pages/zhuyin/ZhuyinLearn";
import ZhuyinTrace from "./pages/zhuyin/ZhuyinTrace";
import ZhuyinMarathon from "./pages/zhuyin/ZhuyinMarathon";
import ZhuyinQuiz from "./pages/zhuyin/ZhuyinQuiz";
import ZhuyinProgress from "./pages/zhuyin/ZhuyinProgress";
import LifeHall from "./pages/LifeHall";
import SpeakHall from "./pages/speak/SpeakHall";
import SpeakLesson from "./pages/speak/SpeakLesson";
import ArtHall from "./pages/art/ArtHall";
import ArtStudio from "./pages/art/ArtStudio";
import ArtWorks from "./pages/art/ArtWorks";
import ArtGallery from "./pages/art/ArtGallery";
import ArtSharePublic from "./pages/art/ArtSharePublic";
import Pricing from "./pages/Pricing";
import { PaymentSuccess, PaymentCancel } from "./pages/PaymentResult";
import StaffLogin from "./pages/staff/StaffLogin";
import StaffDashboard from "./pages/staff/StaffDashboard";

const Loading = () => (
  <div className="min-h-screen paper-bg flex items-center justify-center" data-testid="app-loading">
    <div className="font-brush text-5xl text-[var(--vermilion)] floaty">小書童</div>
  </div>
);

const AuthCallback = () => {
  const done = useRef(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    api
      .post("/auth/session", { session_id: sid })
      .then(({ data }) => {
        setUser(data);
        window.history.replaceState(null, "", "/profiles");
        navigate("/profiles", { replace: true });
      })
      .catch(() => navigate("/", { replace: true }));
  }, [navigate, setUser]);
  return <Loading />;
};

const Protected = ({ children, admin }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/parent" replace />;
  return children;
};

const zr = (C) => <Protected><C /></Protected>;

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
    <ScrollToTop />
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/learn" element={<Learn />} />
      <Route path="/stories" element={<Stories />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/art" element={<Protected><ArtHall /></Protected>} />
      <Route path="/art/free" element={<Protected><ArtStudio /></Protected>} />
      <Route path="/art/a/:activityId" element={<Protected><ArtStudio /></Protected>} />
      <Route path="/art/works" element={<Protected><ArtWorks /></Protected>} />
      <Route path="/art/gallery" element={<Protected><ArtGallery /></Protected>} />
      <Route path="/art/s/:token" element={<ArtSharePublic />} />
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/staff/login" element={<StaffLogin />} />
      <Route path="/staff" element={<StaffDashboard />} />
      <Route path="/dashboard" element={<UserHub />} />
      <Route path="/recordings" element={<Protected><Recordings /></Protected>} />
      <Route path="/profiles" element={<Protected><Profiles /></Protected>} />
      <Route path="/halls" element={<Protected><Halls /></Protected>} />
      <Route path="/life" element={<Protected><LifeHall /></Protected>} />
      <Route path="/speak" element={<Protected><SpeakHall /></Protected>} />
      <Route path="/speak/:topicId" element={<Protected><SpeakLesson /></Protected>} />
      <Route path="/play" element={<Protected><WorldMap /></Protected>} />
      <Route path="/zhuyin" element={zr(ZhuyinRoom)} />
      <Route path="/zhuyin/s/:symbol" element={zr(ZhuyinLearn)} />
      <Route path="/zhuyin/trace" element={zr(ZhuyinTrace)} />
      <Route path="/zhuyin/marathon" element={zr(ZhuyinMarathon)} />
      <Route path="/zhuyin/quiz" element={zr(ZhuyinQuiz)} />
      <Route path="/zhuyin/progress" element={zr(ZhuyinProgress)} />
      <Route path="/listen" element={<Protected><Listen /></Protected>} />
      <Route path="/lesson/:storyId" element={<Protected><LessonPlayer /></Protected>} />
      <Route path="/rewards" element={<Protected><Rewards /></Protected>} />
      <Route path="/parent" element={<Protected><ParentDashboard /></Protected>} />
      <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
};

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
